import React, { useMemo, useState, useEffect } from "react";
import {
  Box,
  Typography,
  Popover,
  TextField,
  List,
  ListItemButton,
  Divider,
  Button,
  Radio,
  RadioGroup,
  FormControlLabel,
} from "@mui/material";
import CalendarMonthOutlinedIcon from "@mui/icons-material/CalendarMonthOutlined";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DateCalendar } from "@mui/x-date-pickers/DateCalendar";
import { PickersDay } from "@mui/x-date-pickers/PickersDay";
import dayjs from "dayjs";

/**
 * Presets matching /api/dashboard/* timeRange query param values.
 */
const PRESETS = [
  { key: "all", label: "All Time" },
  { key: "today", label: "Today" },
  { key: "yesterday", label: "Yesterday" },
  { key: "this_week", label: "This Week" },
  { key: "last_week", label: "Last Week" },
  { key: "this_month", label: "This Month" },
  { key: "last_month", label: "Last Month" },
  { key: "this_quarter", label: "This Quarter" },
  { key: "last_quarter", label: "Last Quarter" },
  { key: "this_year", label: "This Year" },
  { key: "last_year", label: "Last Year" },
  { key: "date_range", label: "Custom Range" },
];

const toApiDate = (d) => (d && d.isValid() ? d.format("YYYY-MM-DD") : "");

const formatInputDate = (d) => (d && d.isValid() ? d.format("MMM D, YYYY") : "");

const getPresetLabel = (key) =>
  PRESETS.find((p) => p.key === key)?.label ?? "This Week";

function RangeDay({ day, start, end, ...other }) {
  const isStart = start && day.isSame(start, "day");
  const isEnd = end && day.isSame(end, "day");
  const inRange =
    start && end && day.isAfter(start, "day") && day.isBefore(end, "day");

  return (
    <PickersDay
      {...other}
      day={day}
      sx={{
        ...(isStart || isEnd
          ? { bgcolor: "#2563eb !important", color: "#fff !important", "&:hover": { bgcolor: "#1d4ed8 !important" } }
          : {}),
        ...(inRange
          ? { bgcolor: "#eff6ff", borderRadius: 0, "&:hover": { bgcolor: "#dbeafe" } }
          : {}),
      }}
    />
  );
}

/**
 * DashboardDateFilter
 *
 * Props:
 *   value: { timeRange: string, startDate?: string, endDate?: string }
 *   onChange: (next) => void  — called with same shape on apply
 */
const DashboardDateFilter = ({ value = { timeRange: "this_week" }, onChange }) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const [draftPreset, setDraftPreset] = useState(value.timeRange ?? "this_week");
  const [draftStart, setDraftStart] = useState(
    value.startDate ? dayjs(value.startDate) : null
  );
  const [draftEnd, setDraftEnd] = useState(
    value.endDate ? dayjs(value.endDate) : null
  );
  const [draftDateType, setDraftDateType] = useState(value.dateType ?? "due_date");
  const [focusedField, setFocusedField] = useState("start");
  const [calendarMonth, setCalendarMonth] = useState(dayjs());

  const open = Boolean(anchorEl);

  useEffect(() => {
    setDraftPreset(value.timeRange ?? "this_week");
    setDraftStart(value.startDate ? dayjs(value.startDate) : null);
    setDraftEnd(value.endDate ? dayjs(value.endDate) : null);
    setDraftDateType(value.dateType ?? "due_date");
  }, [value.timeRange, value.startDate, value.endDate, value.dateType]);

  const handleOpen = (e) => {
    setDraftPreset(value.timeRange ?? "this_week");
    setDraftStart(value.startDate ? dayjs(value.startDate) : null);
    setDraftEnd(value.endDate ? dayjs(value.endDate) : null);
    setDraftDateType(value.dateType ?? "due_date");
    setFocusedField("start");
    setCalendarMonth(dayjs());
    setAnchorEl(e.currentTarget);
  };

  const handleClose = () => setAnchorEl(null);

  const handlePresetClick = (preset) => {
    if (preset.key === "date_range") {
      setDraftPreset("date_range");
      return; // stay open to pick dates
    }
    onChange({ timeRange: preset.key, dateType: draftDateType });
    handleClose();
  };

  const handleDaySelect = (newDay) => {
    if (!newDay) return;
    setDraftPreset("date_range");
    if (focusedField === "start") {
      setDraftStart(newDay);
      setDraftEnd(draftEnd && newDay.isAfter(draftEnd, "day") ? newDay : draftEnd || newDay);
      setFocusedField("end");
    } else {
      setDraftStart(draftStart && newDay.isBefore(draftStart, "day") ? newDay : draftStart || newDay);
      setDraftEnd(newDay);
    }
    setCalendarMonth(newDay);
  };

  const handleApply = () => {
    if (draftPreset === "date_range") {
      onChange({
        timeRange: "date_range",
        startDate: toApiDate(draftStart),
        endDate: toApiDate(draftEnd),
        dateType: draftDateType,
      });
    } else {
      onChange({ timeRange: draftPreset, dateType: draftDateType });
    }
    handleClose();
  };

  const handleClear = () => {
    onChange({ timeRange: "this_week", dateType: "due_date" });
    handleClose();
  };

  const triggerLabel = useMemo(() => {
    if (value.timeRange === "date_range" && value.startDate && value.endDate) {
      const s = dayjs(value.startDate);
      const e = dayjs(value.endDate);
      return `${s.format("MMM D")} – ${e.format("MMM D")}`;
    }
    return null;
  }, [value]);

  const showCalendar = draftPreset === "date_range";

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Box
        onClick={handleOpen}
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1,
          border: "1px solid #d1d5db",
          borderRadius: "8px",
          backgroundColor: "#fff",
          cursor: "pointer",
          px: 1.5,
          py: 0.75,
          transition: "border-color 0.2s",
          "&:hover": { borderColor: "#9ca3af", bgcolor: "#f9fafb" },
          ...(open ? { borderColor: "#2563eb" } : {}),
          color: "#374151",
          fontSize: "0.875rem",
          fontWeight: 500,
        }}
      >
        <CalendarMonthOutlinedIcon sx={{ fontSize: 16, color: "#6b7280" }} />
        <Box sx={{ flex: 1, minWidth: 0, lineHeight: 1.2 }}>
          <Typography sx={{ fontSize: "0.875rem", fontWeight: 500, color: "#374151", whiteSpace: "nowrap" }}>
            {getPresetLabel(value.timeRange ?? "this_week")}
          </Typography>
          {triggerLabel && (
            <Typography sx={{ fontSize: "0.72rem", color: "#6b7280", whiteSpace: "nowrap" }}>
              {triggerLabel}
            </Typography>
          )}
        </Box>
        <KeyboardArrowDownIcon sx={{ fontSize: 18, color: "#6b7280" }} />
      </Box>

      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        slotProps={{ paper: { sx: { borderRadius: 2, mt: 0.75, boxShadow: "0 8px 24px rgba(0,0,0,0.12)", overflow: "hidden" } } }}
      >
        <Box sx={{ display: "flex", flexDirection: "column" }}>
          {/* Filter Field Selection */}
          <Box sx={{ p: 1.5, pb: 1, borderBottom: "1px solid #e8eaed", bgcolor: "#f9fafb" }}>
            <Typography sx={{ fontSize: "0.75rem", fontWeight: 600, color: "#64748b", mb: 0.5, textTransform: "uppercase", letterSpacing: "0.05em" }}>
              Filter By
            </Typography>
            <RadioGroup
              row
              value={draftDateType}
              onChange={(e) => {
                const newType = e.target.value;
                setDraftDateType(newType);
                if (draftPreset !== "date_range") {
                  onChange({ timeRange: draftPreset, dateType: newType });
                }
              }}
              sx={{ "& .MuiFormControlLabel-root": { mr: 3 } }}
            >
              <FormControlLabel
                value="due_date"
                control={<Radio size="small" sx={{ color: "#94a3b8", "&.Mui-checked": { color: "#2563eb" } }} />}
                label={<Typography sx={{ fontSize: "0.85rem", color: "#374151" }}>Due Date</Typography>}
              />
              <FormControlLabel
                value="created_at"
                control={<Radio size="small" sx={{ color: "#94a3b8", "&.Mui-checked": { color: "#2563eb" } }} />}
                label={<Typography sx={{ fontSize: "0.85rem", color: "#374151" }}>Created Date</Typography>}
              />
            </RadioGroup>
          </Box>

          <Box sx={{ display: "flex" }}>
            {/* Calendar — only shown when Custom Range is selected */}
            {showCalendar && (
            <>
              <Box sx={{ p: 2, width: 310 }}>
                <Typography sx={{ fontSize: "0.9rem", fontWeight: 700, color: "#2563eb", mb: 0.4 }}>
                  Custom Range
                </Typography>
                <Typography sx={{ fontSize: "0.72rem", color: "#94a3b8", mb: 1.5 }}>
                  Select start and end dates
                </Typography>

                <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1.5 }}>
                  <TextField
                    size="small"
                    value={formatInputDate(draftStart)}
                    placeholder="From"
                    onClick={(e) => { e.stopPropagation(); setFocusedField("start"); }}
                    InputProps={{ readOnly: true }}
                    sx={{
                      flex: 1,
                      "& .MuiOutlinedInput-root": {
                        borderRadius: "8px", fontSize: "0.85rem", bgcolor: "#fff",
                        ...(focusedField === "start" ? { "& fieldset": { borderColor: "#2563eb", borderWidth: 2 } } : {}),
                      },
                    }}
                  />
                  <Typography sx={{ color: "#94a3b8" }}>–</Typography>
                  <TextField
                    size="small"
                    value={formatInputDate(draftEnd)}
                    placeholder="To"
                    onClick={(e) => { e.stopPropagation(); setFocusedField("end"); }}
                    InputProps={{ readOnly: true }}
                    sx={{
                      flex: 1,
                      "& .MuiOutlinedInput-root": {
                        borderRadius: "8px", fontSize: "0.85rem", bgcolor: "#fff",
                        ...(focusedField === "end" ? { "& fieldset": { borderColor: "#2563eb", borderWidth: 2 } } : {}),
                      },
                    }}
                  />
                </Box>

                <DateCalendar
                  value={focusedField === "start" ? draftStart : draftEnd}
                  referenceDate={calendarMonth}
                  onMonthChange={setCalendarMonth}
                  onChange={handleDaySelect}
                  slots={{ day: RangeDay, leftArrowIcon: ChevronLeftIcon, rightArrowIcon: ChevronRightIcon }}
                  slotProps={{ day: { start: draftStart, end: draftEnd } }}
                  sx={{
                    width: "100%",
                    minHeight: 320,
                    "& .MuiPickersCalendarHeader-label": { fontSize: "0.8rem", fontWeight: 700, color: "#64748b" },
                    "& .MuiDayCalendar-weekDayLabel": { fontSize: "0.75rem", color: "#94a3b8" },
                    "& .MuiPickersDay-root": { fontSize: "0.85rem" },
                  }}
                />

                <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1, pt: 1.5, borderTop: "1px solid #e8eaed" }}>
                  <Button onClick={handleClear} variant="outlined" size="small" sx={{ textTransform: "none", borderColor: "#d1d5db", color: "#374151" }}>
                    Clear
                  </Button>
                  <Button onClick={handleApply} variant="contained" size="small" sx={{ textTransform: "none", bgcolor: "#2563eb", "&:hover": { bgcolor: "#1d4ed8" } }}>
                    Apply
                  </Button>
                </Box>
              </Box>

              <Divider orientation="vertical" flexItem />
            </>
          )}

          {/* Presets list */}
          <Box sx={{ width: 180, display: "flex", flexDirection: "column" }}>
            <List disablePadding sx={{ py: 1, flex: 1 }}>
              {PRESETS.map((preset) => {
                const isActive = (value.timeRange ?? "this_week") === preset.key;
                return (
                  <ListItemButton
                    key={preset.key}
                    selected={isActive}
                    onClick={() => handlePresetClick(preset)}
                    sx={{
                      py: 0.8, px: 2,
                      "&:hover": { bgcolor: "rgba(37,99,235,0.04)" },
                      "&.Mui-selected": { bgcolor: "rgba(37,99,235,0.06)" },
                    }}
                  >
                    <Typography
                      sx={{ fontSize: "0.875rem", fontWeight: isActive ? 700 : 500, color: isActive ? "#2563eb" : "#374151" }}
                    >
                      {preset.label}
                    </Typography>
                  </ListItemButton>
                );
              })}
            </List>

            {!showCalendar && (
              <Box sx={{ p: 1.5, borderTop: "1px solid #e8eaed", display: "flex", justifyContent: "flex-end" }}>
                <Button onClick={handleClear} variant="outlined" size="small" sx={{ textTransform: "none", borderColor: "#d1d5db", color: "#374151" }}>
                  Reset
                </Button>
              </Box>
            )}
          </Box>
        </Box>
        </Box>
      </Popover>
    </LocalizationProvider>
  );
};

export default DashboardDateFilter;
